import { useState, useMemo } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, Button, Input, VStack, Text, useToast, Box, Progress,
  HStack, Icon, IconButton,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import FileManagerUploadHelper from "../../../../utils/FileManagerUploadHelper";

const FilePreview = ({ file, onRemove, texts = {} }) => {
  const fileManagerTexts = texts?.fileManager || {};
  
  return (
    <HStack
      key={file.name} w="100%" p={2} spacing={4}
      borderWidth="1px" borderRadius="md"
      sx={{
        borderColor: 'var(--upload-file-preview-border)',
        bg: 'var(--upload-file-preview-bg)'
      }}
    >
      <Box flex="1" isTruncated>
        <Text fontSize="sm" noOfLines={1}>- {file.name}</Text>
      </Box>
      <IconButton 
        icon={<FiX />} 
        size="xs" 
        isRound 
        variant="ghost"
        aria-label={fileManagerTexts.ariaLabels?.removeFile || "Remove file"}
        onClick={onRemove}
        sx={{
            color: 'var(--upload-file-remove-icon)',
            _hover: { 
                bg: 'var(--upload-file-remove-hover-bg)', 
                color: 'var(--upload-file-remove-hover-icon)'
            }
        }}
      />
    </HStack>
  );
};

export default function UploadFiles({ IsOpen, OnClose, OnRefetch, ParentId, ContractId, texts = {} }) {
  const fileManagerTexts = texts?.fileManager || {};
  const generalTexts = texts?.general || {};
  
  const toast = useToast();
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const fileList = useMemo(() => Array.from(files), [files]);

  const handleFileChange = (e) => {
    if (e.target.files) { 
      setFiles(e.target.files); 
    }
  };

  const removeFile = (fileToRemove) => {
    const newFiles = fileList.filter(file => file !== fileToRemove);
    setFiles(newFiles);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) return;
    setIsUploading(true);
    setProgress(0);

    try {
      await FileManagerUploadHelper.StartUploadQueue({
        Files: fileList,
        ExtraOptions: {
            ContractId,
            ParentId,
        },
        OnOverallProgress: (overallProgress) => {
          setProgress(overallProgress);
        },
      });
      
      toast({ 
        title: fileManagerTexts.uploadCompleted || "Upload completed successfully!", 
        status: "success", 
        duration: 4000 
      });
      OnRefetch();
      handleClose();

    } catch (error) {
      console.error("Upload process failed:", error);
      toast({ 
        title: fileManagerTexts.uploadError || "An error occurred during upload.", 
        description: error.message, 
        status: "error" 
      });
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setProgress(0);
    setIsUploading(false);
    OnClose();
  };

  return (
    <Modal isOpen={IsOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent sx={{ bg: 'var(--upload-modal-bg)', color: 'var(--upload-modal-text)' }}>
        <ModalHeader borderBottomWidth="1px" sx={{ borderColor: 'var(--upload-modal-border)' }}>
          {fileManagerTexts.uploadFiles || "Upload Files"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <VStack spacing={5}>
            <Button 
              as="label" 
              htmlFor="file-upload" 
              cursor="pointer"
              w="100%"
              sx={{ bg: 'var(--upload-btn-bg)', _hover: { bg: 'var(--upload-btn-hover-bg)' } }}
            >
              {fileManagerTexts.chooseFiles || "Choose Files"}
            </Button>
            <Input 
              id="file-upload"
              type="file" 
              multiple 
              display="none"
              onChange={handleFileChange}
              onClick={(e) => (e.target.value = null)}
            />

            {fileList.length > 0 && (
              <VStack align="start" w="100%" maxH="200px" overflowY="auto" spacing={3} p={1}>
                <Text fontWeight="bold">
                  {fileManagerTexts.selectedFiles || "Selected files"}: {fileList.length}
                </Text>
                {fileList.map((file, index) => (
                  <FilePreview 
                    key={`${file.name}-${index}`} 
                    file={file} 
                    onRemove={() => removeFile(file)}
                    texts={texts}
                  />
                ))}
              </VStack>
            )}

            {isUploading && (
              <Box w="100%" pt={2}>
                <Progress 
                  value={progress} 
                  size="md" 
                  hasStripe 
                  isAnimated
                  sx={{
                    '> div': { background: 'var(--upload-progress-filled-bg)' },
                    background: 'var(--upload-progress-track-bg)'
                  }}
                />
                <Text textAlign="center" mt={2} fontSize="sm">{progress}%</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" sx={{ borderColor: 'var(--upload-modal-border)' }}>
          <Button 
            variant="ghost" 
            mr={3} 
            onClick={handleClose} 
            isDisabled={isUploading} 
            sx={{ _hover: { bg: 'var(--modal-btn-ghost-hover-bg)' } }}
          >
            {generalTexts.cancel || "Cancel"}
          </Button>
          <Button
            onClick={handleUpload}
            isLoading={isUploading}
            isDisabled={fileList.length === 0 || isUploading}
            loadingText={fileManagerTexts.uploading || "Uploading..."}
            sx={{
              bg: 'var(--upload-submit-bg)',
              color: 'var(--btn-special-text)',
              _hover: { bg: 'var(--upload-submit-hover-bg)' }
            }}
          >
            {fileManagerTexts.upload || "Upload"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}